import { useLazyQuery, useQuery } from '@apollo/client/react';
import { useTheme } from '@emotion/react';
import { useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { Alert, Box, Button, Drawer, Grid, Header, Icon } from '~/components/blocks';
import {
  GetCurrentPractitionerDocument,
  GetSettingsPractitionerDocument,
  GetSettingsPractitionerQuery,
  SettingsPractitionerFragment,
} from '~/graphql/generated/graphql';
import { getMe } from '~/graphql/utility';
import { usePractitionerRole } from '~/hooks/use-practitioner-role';
import { useScrollToTop } from '~/hooks/use-scroll-to-top';
import { resetPasswordModalState } from '~/state/settings_practitioners/atoms';

import { PractitionerForm } from '../PractitionerForm';
import { Fields } from '../PractitionerForm/types';
import { useUpdatePractitioner } from './use-update_practitioner';

type Props = {
  open: boolean;
  privilege?: boolean | null;
  practitionerId?: string | null;
  onClose: () => void;
  onOpenModal: () => void;
  onTransitionEnd: () => void;
};

const useGetIsLoginedPractitoner = (data?: GetSettingsPractitionerQuery) => {
  const { data: currentPractitionerData } = useQuery(GetCurrentPractitionerDocument);
  if (!data || !data.node || !currentPractitionerData) return false;

  return (data.node as SettingsPractitionerFragment).id === getMe(currentPractitionerData)?.id;
};

const formatPractitioner = (data: GetSettingsPractitionerQuery) => {
  return data.node ? (data.node as SettingsPractitionerFragment) : null;
};

const makeInitialValues = (practitioner: SettingsPractitionerFragment | null): Fields => {
  if (practitioner) {
    return {
      familyName: practitioner.familyName,
      givenName: practitioner.givenName,
      phoneticFamilyName: practitioner.phoneticFamilyName,
      phoneticGivenName: practitioner.phoneticGivenName,
      username: practitioner.authentication.username,
      password: '',
      passwordConfirmation: '',
      roles: practitioner.roles,
    };
  } else {
    return {
      familyName: '',
      givenName: '',
      phoneticFamilyName: '',
      phoneticGivenName: '',
      username: '',
      password: '',
      passwordConfirmation: '',
      roles: [],
    };
  }
};

export const EditDrawer = (props: Props) => {
  const { onClose } = props;
  const theme = useTheme();
  const { verifyRole } = usePractitionerRole();
  const { error, updating, update, resetError } = useUpdatePractitioner();
  const [containerRef, scrollToTop] = useScrollToTop<HTMLDivElement>();
  const formRef = useRef<RefAttributeType<typeof PractitionerForm>>(null);
  const setState = useSetAtom(resetPasswordModalState);

  const isAdmin = verifyRole('admin');

  const [getPractitioner, { data }] = useLazyQuery(GetSettingsPractitionerDocument, {
    fetchPolicy: 'no-cache',
  });

  const isLoginedPractitioner = useGetIsLoginedPractitoner(data);
  const isUpdatedAuth = isLoginedPractitioner && isAdmin;
  const practitioner = data ? formatPractitioner(data) : null;
  const initialValues = useMemo<Fields>(() => makeInitialValues(practitioner), [practitioner]);

  const handleResetPassword = useCallback(() => {
    if (practitioner) {
      setState({ isOpen: true, practitionerId: practitioner.id });
    }
  }, [practitioner, setState]);
  const handleSend = useCallback(() => {
    if (!formRef.current) return;
    formRef.current.submitForm();
  }, []);
  const handleSubmit = useCallback(
    async (values: Fields) => {
      if (!practitioner) return;

      try {
        await update(values, practitioner.id, practitioner.roles);
        onClose();
      } catch {
        scrollToTop();
      }
    },
    [onClose, practitioner, scrollToTop, update],
  );

  useEffect(() => {
    if (props.open && props.practitionerId) {
      resetError();
      getPractitioner({
        variables: {
          id: props.practitionerId,
        },
      });
    }
  }, [getPractitioner, props.open, props.practitionerId, resetError]);

  return (
    <Drawer open={props.open} onClose={onClose} onTransitionEnd={props.onTransitionEnd}>
      <Grid height="100%" gridTemplateRows="min-content 1fr min-content">
        <Header attached="drawer">スタッフ</Header>
        <Box overflow="auto" mb={theme.space.xl} ref={containerRef}>
          {error && (
            <Alert status="error" m={`0 ${theme.space.xl} ${theme.space.m}`} whiteSpace="pre-wrap">
              {error}
            </Alert>
          )}
          <PractitionerForm
            ref={formRef}
            initialValues={initialValues}
            privilege={!!props.privilege}
            disabled={updating}
            updatedAuth={isUpdatedAuth}
            username={practitioner?.authentication.username}
            onSubmit={handleSubmit}
            onResetPassword={!isUpdatedAuth ? handleResetPassword : undefined}
          />
          {!isLoginedPractitioner && (
            <Button
              use="white"
              size="s"
              m={`${theme.space.l} ${theme.space.xl} 0`}
              onClick={props.onOpenModal}
            >
              <Icon size="s" icon="trash" />
              スタッフ削除
            </Button>
          )}
        </Box>
        <Drawer.Footer>
          <Button wide="fill" use="base" size="l" loading={updating} onClick={handleSend}>
            保存
          </Button>
        </Drawer.Footer>
      </Grid>
    </Drawer>
  );
};
